import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import PdfViewer from './PdfViewer';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  FileText, Upload, CheckCircle2, XCircle, Clock as ClockIcon, AlertTriangle,
  Download, Eye, RotateCcw, ChevronDown, ChevronUp, ShieldAlert, Image as ImageIcon,
  Plus, Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOC_TYPES = [
  { key: 'sia_door_supervisor', label: 'SIA Door Supervisor Licence' },
  { key: 'cctv', label: 'CCTV Licence' },
  { key: 'proof_of_address', label: 'Proof of Address' },
  { key: 'first_aid', label: 'First Aid Certificate' },
  { key: 'act_awareness', label: 'ACT Awareness Certificate' },
  { key: 'scan_training', label: 'SCaN Training Certificate or Screenshot' },
];

// Categories that accept multiple files concurrently (each upload appends
// rather than replaces). Kept in sync with backend MULTI_FILE_DOC_TYPES.
const MULTI_FILE_TYPES = new Set([
  'sia_door_supervisor', 'cctv', 'proof_of_address',
  'first_aid', 'act_awareness', 'scan_training',
]);

const DEFAULT_MANDATORY = ['sia_door_supervisor', 'proof_of_address', 'act_awareness', 'scan_training'];

const STATUS_BADGE = {
  pending_review: { label: 'PENDING REVIEW', cls: 'bg-amber-500/15 text-amber-400 border-amber-500/40' },
  approved:       { label: 'APPROVED',       cls: 'bg-green-500/15 text-green-400 border-green-500/40' },
  rejected:       { label: 'REJECTED',       cls: 'bg-red-500/15 text-red-400 border-red-500/40' },
  expired:        { label: 'EXPIRED',        cls: 'bg-red-500/15 text-red-400 border-red-500/40' },
};

function expiryColor(dateStr) {
  if (!dateStr) return 'text-muted-foreground';
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = new Date(dateStr);
  const days = Math.floor((d - today) / (1000 * 60 * 60 * 24));
  if (days < 0) return 'text-red-400';
  if (days <= 30) return 'text-amber-400';
  return 'text-green-400';
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  try { return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return dateStr; }
}

/**
 * Compliance Documents
 *
 * Props:
 *  - mode: 'admin' | 'employee'
 *  - employeeId (admin only): the user whose docs we are managing
 */
export default function ComplianceDocuments({ mode = 'employee', employeeId = null }) {
  const isAdmin = mode === 'admin';
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mandatory, setMandatory] = useState(new Set());
  const [historyOpen, setHistoryOpen] = useState({});  // docType -> bool

  // Upload state
  const [uploadType, setUploadType] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadExpiry, setUploadExpiry] = useState('');
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef(null);

  // Review modal (admin)
  const [reviewModal, setReviewModal] = useState({ open: false, doc: null, kind: null }); // kind: reject|reupload
  const [reviewReason, setReviewReason] = useState('');
  const [reviewSaving, setReviewSaving] = useState(false);

  // Preview modal
  const [previewModal, setPreviewModal] = useState({ open: false, blobUrl: null, mime: null, fileName: null });

  const listUrl = isAdmin ? `${API}/admin/employees/${employeeId}/documents` : `${API}/users/me/documents`;
  const downloadUrl = (id) => isAdmin ? `${API}/admin/documents/${id}/download` : `${API}/users/me/documents/${id}/download`;

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const cfgPromise = isAdmin
        ? axios.get(`${API}/admin/documents/config`)
        : axios.get(`${API}/users/me/documents/config`).catch(() => ({ data: { mandatory: DEFAULT_MANDATORY } }));
      const [docsRes, cfgRes] = await Promise.all([axios.get(listUrl), cfgPromise]);
      setDocs(docsRes.data || []);
      setMandatory(new Set(cfgRes.data?.mandatory || DEFAULT_MANDATORY));
    } catch (e) {
      console.error(e);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [listUrl, isAdmin]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Group docs by type. For single-file categories the latest current row
  // is the "current" and older versions go to history. Multi-file categories
  // expose EVERY current version in `currents` (sorted oldest→newest).
  const byType = {};
  for (const d of docs) {
    if (!byType[d.doc_type]) byType[d.doc_type] = { currents: [], history: [] };
    if (d.is_current_version) byType[d.doc_type].currents.push(d);
    else byType[d.doc_type].history.push(d);
  }
  for (const k of Object.keys(byType)) {
    byType[k].currents.sort((a, b) => (a.uploaded_at || '').localeCompare(b.uploaded_at || ''));
    if (!MULTI_FILE_TYPES.has(k)) {
      // single-file: collapse to one "current"
      const latest = byType[k].currents[byType[k].currents.length - 1] || null;
      byType[k].current = latest;
    }
  }

  const handleFileSelected = (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('File exceeds 10 MB limit'); return; }
    const ok = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'].includes(file.type);
    if (!ok) { toast.error('Only PDF, JPG, PNG accepted'); return; }
    setUploadFile(file);
  };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFileSelected(f);
  };

  const doUpload = async () => {
    if (!uploadFile || !uploadType) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('doc_type', uploadType);
      if (uploadExpiry) fd.append('expiry_date', uploadExpiry);
      fd.append('file', uploadFile);
      await axios.post(`${API}/users/me/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Document uploaded — pending admin review');
      setUploadType(null); setUploadFile(null); setUploadExpiry('');
      await fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const openPreview = async (doc) => {
    try {
      const res = await axios.get(downloadUrl(doc.id), { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      setPreviewModal({ open: true, blobUrl: url, mime: doc.mime_type, fileName: doc.file_name });
    } catch {
      toast.error('Failed to load file');
    }
  };

  const closePreview = () => {
    if (previewModal.blobUrl) URL.revokeObjectURL(previewModal.blobUrl);
    setPreviewModal({ open: false, blobUrl: null, mime: null, fileName: null });
  };

  const triggerDownload = async (doc) => {
    try {
      const res = await axios.get(downloadUrl(doc.id), { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = doc.file_name || 'document';
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  };

  const doApprove = async (doc) => {
    try {
      await axios.post(`${API}/admin/documents/${doc.id}/approve`);
      toast.success('Document approved');
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Approval failed'); }
  };

  const doReviewSubmit = async () => {
    const { doc, kind } = reviewModal;
    if (!doc || !kind) return;
    if (kind === 'reject' && !reviewReason.trim()) {
      toast.error('Rejection reason is required'); return;
    }
    setReviewSaving(true);
    try {
      const ep = kind === 'reject' ? 'reject' : 'request-reupload';
      await axios.post(`${API}/admin/documents/${doc.id}/${ep}`, { reason: reviewReason });
      toast.success(kind === 'reject' ? 'Document rejected' : 'Re-upload requested');
      setReviewModal({ open: false, doc: null, kind: null }); setReviewReason('');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setReviewSaving(false);
    }
  };

  const doRemoveOwn = async (doc) => {
    if (!window.confirm(`Remove "${doc.file_name}" from this category?`)) return;
    try {
      await axios.delete(`${API}/users/me/documents/${doc.id}`);
      toast.success('File removed');
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Remove failed');
    }
  };

  if (loading) return <div className="py-10 text-center text-muted-foreground animate-pulse">Loading documents…</div>;

  return (
    <Card className="bg-card border-border rounded-sm" data-testid="compliance-documents">
      <CardHeader className="border-b border-border pb-4">
        <CardTitle className="text-sm font-barlow tracking-wider uppercase flex items-center gap-2">
          <ShieldAlert className="w-4 h-4 text-primary" /> Compliance Documents
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0 divide-y divide-border">
        {!isAdmin && (
          <div className="px-3 sm:px-4 py-3 bg-amber-500/5 text-xs text-amber-300 flex items-start gap-2">
            <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <p>
              Categories marked <strong>REQUIRED</strong> are mandatory. You can attach
              multiple files to the same category — use <strong>Add Another File</strong>.
            </p>
          </div>
        )}
        {DOC_TYPES.map((t) => {
          const grouped = byType[t.key] || { currents: [], history: [] };
          const isMulti = MULTI_FILE_TYPES.has(t.key);
          const currents = grouped.currents || [];
          const isMandatory = mandatory.has(t.key);
          const hasAny = currents.length > 0;
          const showHistory = historyOpen[t.key];
          return (
            <div key={t.key} className={cn('p-3 sm:p-4', !isAdmin && isMandatory && !hasAny && 'border-l-4 border-l-red-500 bg-red-500/5')} data-testid={`doc-row-${t.key}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="flex-1 min-w-[200px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-medium">{t.label}</p>
                    {isMandatory && <Badge className="text-[10px] bg-primary/15 text-primary border-primary/40 rounded-sm" data-testid={`required-badge-${t.key}`}>REQUIRED</Badge>}
                    {!hasAny && isMandatory && <Badge className="text-[10px] bg-red-500/15 text-red-400 border-red-500/40 rounded-sm">MISSING</Badge>}
                    {isMulti && hasAny && <Badge variant="outline" className="text-[10px] rounded-sm border-border text-muted-foreground">{currents.length} file{currents.length === 1 ? '' : 's'}</Badge>}
                  </div>
                  {!hasAny && (
                    <p className="mt-1 text-xs text-muted-foreground italic">Not uploaded yet</p>
                  )}
                </div>
                {!isAdmin && (
                  <Button
                    size="sm"
                    onClick={() => { setUploadType(t.key); setUploadFile(null); setUploadExpiry(''); }}
                    className={cn(
                      'rounded-sm h-8 bg-primary text-primary-foreground hover:bg-yellow-300',
                      isMandatory && !hasAny && 'animate-pulse font-semibold'
                    )}
                    data-testid={`upload-${t.key}`}
                  >
                    {isMulti && hasAny ? <Plus className="w-3.5 h-3.5 mr-1" /> : <Upload className="w-3.5 h-3.5 mr-1" />}
                    {isMulti ? (hasAny ? 'Add Another File' : 'Upload') : (hasAny ? 'Replace' : 'Upload')}
                  </Button>
                )}
              </div>

              {/* File list */}
              {hasAny && (
                <ul className="mt-3 space-y-2" data-testid={`files-${t.key}`}>
                  {currents.map((cur) => {
                    const sb = STATUS_BADGE[cur.status];
                    const expCls = expiryColor(cur.expiry_date);
                    return (
                      <li
                        key={cur.id}
                        className="rounded-sm border border-border/60 bg-background/40 px-3 py-2 flex flex-wrap items-start gap-3"
                        data-testid={`file-${cur.id}`}
                      >
                        <div className="flex-1 min-w-[180px] text-xs">
                          <p className="text-sm text-foreground font-medium break-all">
                            {cur.file_name}
                            <span className="text-muted-foreground font-normal ml-2">v{cur.version}</span>
                          </p>
                          <p className="text-muted-foreground mt-0.5">
                            Uploaded {formatDate(cur.uploaded_at)}
                            {cur.expiry_date && <> · <span className={expCls}>expires {formatDate(cur.expiry_date)}</span></>}
                          </p>
                          {cur.status === 'rejected' && cur.rejection_reason && (
                            <p className="text-red-400 mt-0.5">Reason: {cur.rejection_reason}</p>
                          )}
                          {cur.reupload_reason && cur.status !== 'approved' && (
                            <p className="text-amber-400 mt-0.5">Re-upload requested: {cur.reupload_reason}</p>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          {sb && (<Badge className={cn('text-[10px] rounded-sm border', sb.cls)}>{sb.label}</Badge>)}
                          <Button size="sm" variant="outline" onClick={() => openPreview(cur)} className="rounded-sm h-8" data-testid={`preview-${cur.id}`}>
                            <Eye className="w-3.5 h-3.5 mr-1" /> Preview
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => triggerDownload(cur)} className="rounded-sm h-8">
                            <Download className="w-3.5 h-3.5" />
                          </Button>
                          {isAdmin && cur.status !== 'approved' && (
                            <Button size="sm" onClick={() => doApprove(cur)} className="rounded-sm h-8 bg-green-600 hover:bg-green-500 text-white" data-testid={`approve-${cur.id}`}>
                              <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
                            </Button>
                          )}
                          {isAdmin && cur.status !== 'rejected' && (
                            <Button size="sm" variant="outline" onClick={() => { setReviewModal({ open: true, doc: cur, kind: 'reject' }); setReviewReason(''); }} className="rounded-sm h-8 border-red-500/40 text-red-400 hover:bg-red-500/10" data-testid={`reject-${cur.id}`}>
                              <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                            </Button>
                          )}
                          {isAdmin && (
                            <Button size="sm" variant="outline" onClick={() => { setReviewModal({ open: true, doc: cur, kind: 'reupload' }); setReviewReason(''); }} className="rounded-sm h-8 border-amber-500/40 text-amber-400 hover:bg-amber-500/10">
                              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Re-upload
                            </Button>
                          )}
                          {!isAdmin && isMulti && (
                            <Button size="sm" variant="ghost" onClick={() => doRemoveOwn(cur)} className="rounded-sm h-8 text-red-400 hover:text-red-300 hover:bg-red-500/10" data-testid={`remove-${cur.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}

              {grouped.history.length > 0 && (
                <button onClick={() => setHistoryOpen((s) => ({ ...s, [t.key]: !s[t.key] }))} className="mt-2 text-[11px] text-muted-foreground hover:text-foreground flex items-center gap-1">
                  {showHistory ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                  {showHistory ? 'Hide' : 'View'} history ({grouped.history.length} older version{grouped.history.length !== 1 ? 's' : ''})
                </button>
              )}
              {showHistory && (
                <div className="mt-2 ml-4 space-y-1 border-l-2 border-border pl-3">
                  {grouped.history.map((h) => (
                    <div key={h.id} className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">v{h.version} · {h.file_name} · {formatDate(h.uploaded_at)} · {STATUS_BADGE[h.status]?.label || h.status}</span>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => openPreview(h)}><Eye className="w-3 h-3" /></Button>
                        <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => triggerDownload(h)}><Download className="w-3 h-3" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </CardContent>

      {/* Upload dialog (employee mode only) */}
      <Dialog open={!!uploadType} onOpenChange={(o) => !o && setUploadType(null)}>
        <DialogContent data-testid="upload-dialog">
          <DialogHeader>
            <DialogTitle>Upload {DOC_TYPES.find((t) => t.key === uploadType)?.label}</DialogTitle>
            <DialogDescription>
              Step 1: tap the box below and choose a photo or PDF of your document (you can take a photo with your phone). Step 2: press Upload. Max 10 MB.
            </DialogDescription>
          </DialogHeader>
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
            className={cn(
              'border-2 border-dashed rounded-sm p-6 text-center cursor-pointer transition-colors',
              dragOver ? 'border-primary bg-primary/5' : 'border-border'
            )}
            data-testid="upload-dropzone"
          >
            <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-sm">{uploadFile ? uploadFile.name : 'Drag & drop or click to select'}</p>
            {uploadFile && <p className="text-xs text-muted-foreground mt-1">{(uploadFile.size / 1024 / 1024).toFixed(2)} MB</p>}
            <input
              ref={fileInputRef} type="file" className="hidden" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/*"
              onChange={(e) => handleFileSelected(e.target.files?.[0])}
              data-testid="upload-file-input"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="expiry" className="text-xs uppercase tracking-wider text-muted-foreground">Expiry date (optional)</Label>
            <Input id="expiry" type="date" value={uploadExpiry} onChange={(e) => setUploadExpiry(e.target.value)} className="rounded-sm" data-testid="upload-expiry" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadType(null)} disabled={uploading} className="rounded-sm">Cancel</Button>
            <Button onClick={doUpload} disabled={!uploadFile || uploading} className="rounded-sm bg-primary text-primary-foreground hover:bg-yellow-300" data-testid="confirm-upload-btn">
              {uploading ? 'Uploading…' : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin review modal */}
      <Dialog open={reviewModal.open} onOpenChange={(o) => !o && setReviewModal({ open: false, doc: null, kind: null })}>
        <DialogContent data-testid="review-modal">
          <DialogHeader>
            <DialogTitle>{reviewModal.kind === 'reject' ? 'Reject document' : 'Request re-upload'}</DialogTitle>
            <DialogDescription>
              {reviewModal.kind === 'reject' ? 'Reason is required and will be visible to the employee.' : 'Tell the employee why a new version is needed.'}
            </DialogDescription>
          </DialogHeader>
          <Textarea value={reviewReason} onChange={(e) => setReviewReason(e.target.value)} rows={4} className="rounded-sm" placeholder="e.g. Photo is blurry, expiry date is illegible…" data-testid="review-reason-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewModal({ open: false, doc: null, kind: null })} disabled={reviewSaving} className="rounded-sm">Cancel</Button>
            <Button onClick={doReviewSubmit} disabled={reviewSaving} className="rounded-sm bg-primary text-primary-foreground hover:bg-yellow-300" data-testid="confirm-review-btn">
              {reviewSaving ? 'Saving…' : 'Submit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview modal */}
      <Dialog open={previewModal.open} onOpenChange={(o) => !o && closePreview()}>
        <DialogContent className="max-w-4xl h-[80vh] flex flex-col" data-testid="preview-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {previewModal.mime?.startsWith('image/') ? <ImageIcon className="w-4 h-4" /> : <FileText className="w-4 h-4" />}
              {previewModal.fileName}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-auto bg-zinc-950 rounded-sm">
            {previewModal.blobUrl && previewModal.mime?.startsWith('image/') && (
              <img src={previewModal.blobUrl} alt={previewModal.fileName} className="w-full h-full object-contain" />
            )}
            {previewModal.blobUrl && previewModal.mime === 'application/pdf' && (
              <div className="p-2">
                <PdfViewer url={previewModal.blobUrl} />
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
